import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api-client';

// Types
interface Contract {
  id: string;
  code: string;
  organization_id: string;
  type: 'service' | 'maintenance' | 'warranty' | 'lease' | 'insurance' | 'support' | 'subscription' | 'other';
  title: string;
  description?: string;
  supplier_id?: string;
  supplier_name?: string;
  supplier_contact?: string;
  supplier_email?: string;
  supplier_phone?: string;
  scope?: string;
  coverage_type?: 'full' | 'partial' | 'limited' | 'custom';
  equipment_ids?: string[];
  equipment_categories?: string[];
  start_date: string;
  end_date: string;
  signature_date?: string;
  notice_period_days?: number;
  auto_renewal?: boolean;
  renewal_notice_days?: number;
  renewal_count: number;
  total_amount: number;
  currency: string;
  payment_frequency: 'one_time' | 'monthly' | 'quarterly' | 'semi_annual' | 'annual';
  payment_amount: number;
  next_payment_date?: string;
  payment_terms?: string;
  penalty_clause?: string;
  termination_clause?: string;
  service_level_agreement?: {
    response_time?: string;
    resolution_time?: string;
    availability?: string;
    penalties?: string;
  };
  interventions_included?: number;
  interventions_used: number;
  hours_included?: number;
  hours_used: number;
  status: 'draft' | 'pending_signature' | 'active' | 'suspended' | 'expired' | 'terminated' | 'renewed';
  alert_30_days: boolean;
  alert_60_days: boolean;
  alert_90_days: boolean;
  contract_number?: string;
  attachments?: Array<{
    file_name: string;
    file_url: string;
    file_size: number;
    file_type: string;
    uploaded_at: string;
    uploaded_by: string;
  }>;
  contract_manager_id?: string;
  contract_manager_name?: string;
  notes?: string;
  termination_reason?: string;
  termination_date?: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by?: string;
}

interface ContractListParams {
  type?: string;
  status?: string;
  supplierId?: string;
  expiringIn?: number;
  limit?: number;
  offset?: number;
}

interface ContractListResponse {
  items: Contract[];
  total: number;
}

interface ContractStats {
  total: number;
  byType: Record<string, number>;
  byStatus: Record<string, number>;
  totalValue: number;
  activeValue: number;
  expiringIn30Days: number;
  expiringIn60Days: number;
  expiringIn90Days: number;
  expired: number;
}

// Query keys
export const contractKeys = {
  all: ['contracts'] as const,
  lists: () => [...contractKeys.all, 'list'] as const,
  list: (filters: ContractListParams) => [...contractKeys.lists(), filters] as const,
  details: () => [...contractKeys.all, 'detail'] as const,
  detail: (id: string) => [...contractKeys.details(), id] as const,
  stats: () => [...contractKeys.all, 'stats'] as const,
  expiring: (days?: number) => [...contractKeys.all, 'expiring', days] as const,
  expired: () => [...contractKeys.all, 'expired'] as const,
};

// Hooks
export function useContractList(params: ContractListParams = {}) {
  return useQuery({
    queryKey: contractKeys.list(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      if (params.type) searchParams.set('type', params.type);
      if (params.status) searchParams.set('status', params.status);
      if (params.supplierId) searchParams.set('supplier_id', params.supplierId);
      if (params.expiringIn) searchParams.set('expiring_in', params.expiringIn.toString());
      if (params.limit) searchParams.set('limit', params.limit.toString());
      if (params.offset) searchParams.set('offset', params.offset.toString());

      const response = await apiClient.get<ContractListResponse>(
        `/contracts?${searchParams.toString()}`
      );
      return response.data;
    },
  });
}

export function useContractDetail(id: string) {
  return useQuery({
    queryKey: contractKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<{ contract: Contract }>(
        `/contracts/${id}`
      );
      return response.data.contract;
    },
    enabled: !!id,
  });
}

export function useContractStats() {
  return useQuery({
    queryKey: contractKeys.stats(),
    queryFn: async () => {
      const response = await apiClient.get<ContractStats>('/contracts/stats');
      return response.data;
    },
  });
}

export function useContractsExpiring(days: number = 30) {
  return useQuery({
    queryKey: contractKeys.expiring(days),
    queryFn: async () => {
      const response = await apiClient.get<ContractListResponse>(
        `/contracts/expiring?days=${days}`
      );
      return response.data;
    },
  });
}

export function useContractsExpired() {
  return useQuery({
    queryKey: contractKeys.expired(),
    queryFn: async () => {
      const response = await apiClient.get<ContractListResponse>('/contracts/expired');
      return response.data;
    },
  });
}

export function useCreateContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Contract>) => {
      const response = await apiClient.post<{ contract: Contract }>(
        '/contracts',
        data
      );
      return response.data.contract;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.stats() });
    },
  });
}

export function useUpdateContract(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Contract>) => {
      const response = await apiClient.put<{ contract: Contract }>(
        `/contracts/${id}`,
        data
      );
      return response.data.contract;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contractKeys.stats() });
    },
  });
}

export function useDeleteContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/contracts/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.stats() });
    },
  });
}

export function useActivateContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<{ contract: Contract }>(
        `/contracts/${id}/activate`
      );
      return response.data.contract;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contractKeys.stats() });
    },
  });
}

export function useSuspendContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const response = await apiClient.post<{ contract: Contract }>(
        `/contracts/${id}/suspend`,
        { reason }
      );
      return response.data.contract;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contractKeys.stats() });
    },
  });
}

export function useTerminateContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await apiClient.post<{ contract: Contract }>(
        `/contracts/${id}/terminate`,
        { reason }
      );
      return response.data.contract;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contractKeys.stats() });
    },
  });
}

export function useRenewContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, new_end_date, new_amount }: { id: string; new_end_date: string; new_amount?: number }) => {
      const response = await apiClient.post<{ contract: Contract }>(
        `/contracts/${id}/renew`,
        { new_end_date, new_amount }
      );
      return response.data.contract;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contractKeys.stats() });
    },
  });
}

export function useTrackIntervention() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<{ contract: Contract }>(
        `/contracts/${id}/track-intervention`
      );
      return response.data.contract;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) });
    },
  });
}

export function useTrackHours() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, hours }: { id: string; hours: number }) => {
      const response = await apiClient.post<{ contract: Contract }>(
        `/contracts/${id}/track-hours`,
        { hours }
      );
      return response.data.contract;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) });
    },
  });
}
